import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { 
  User, 
  Post, 
  Message, 
  Conversation, 
  CreatePostRequest,
  LoginRequest,
  RegisterRequest,
  PaginatedResponse,
  authApi,
  userApi,
  postsApi,
  messagingApi,
  uploadApi,
} from '../services/api';

// Query Keys
export const queryKeys = {
  user: (userId: string) => ['user', userId],
  userPosts: (userId: string) => ['userPosts', userId],
  posts: (filters?: any) => ['posts', filters],
  post: (postId: string) => ['post', postId],
  conversations: () => ['conversations'],
  messages: (conversationId: string) => ['messages', conversationId],
};

// Authentication Hooks
export const useLogin = (options?: UseMutationOptions<{ 
  user: User; 
  accessToken: string; 
  refreshToken: string; 
  idToken: string; 
  expiresIn: number; 
}, Error, LoginRequest>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (credentials: LoginRequest) => {
      return authApi.login(credentials);
    },
    onSuccess: (data) => {
      // Store auth data
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('idToken', data.idToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      // Update user query cache
      queryClient.setQueryData(queryKeys.user(data.user.userId), data.user);
    },
    ...options,
  });
};

export const useRegister = (options?: UseMutationOptions<{ user: User; message: string }, Error, RegisterRequest>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (userData: RegisterRequest) => {
      return authApi.register(userData);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.user(data.user.userId), data.user);
    },
    ...options,
  });
};

// User Hooks
export const useUser = (userId: string, options?: UseQueryOptions<User, Error>) => {
  return useQuery({
    queryKey: queryKeys.user(userId),
    queryFn: async () => {
      return userApi.getProfile(userId);
    },
    enabled: !!userId,
    ...options,
  });
};

export const useUpdateUser = (options?: UseMutationOptions<User, Error, { userId: string; updates: Partial<User> }>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ userId, updates }: { userId: string; updates: Partial<User> }) => {
      return userApi.updateProfile(userId, updates);
    },
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(queryKeys.user(updatedUser.userId), updatedUser);
      
      // Update user in localStorage if it's the current user
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (currentUser.userId === updatedUser.userId) {
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    },
    ...options,
  });
};

// Posts Hooks
export const usePosts = (filters?: {
  category?: string;
  minPrice?: number;
  maxPrice?: number;
  condition?: string;
  location?: string;
  search?: string;
  sortBy?: 'date' | 'price' | 'views';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  nextToken?: string;
}) => {
  return useQuery({
    queryKey: queryKeys.posts(filters),
    queryFn: async (): Promise<PaginatedResponse<Post>> => {
      return postsApi.getPosts(filters);
    },
  });
};

export const usePost = (postId: string, options?: UseQueryOptions<Post, Error>) => {
  return useQuery({
    queryKey: queryKeys.post(postId),
    queryFn: async () => {
      return postsApi.getPost(postId);
    },
    enabled: !!postId,
    ...options,
  });
};

export const useUserPosts = (userId: string, options?: UseQueryOptions<PaginatedResponse<Post>, Error>) => {
  return useQuery({
    queryKey: queryKeys.userPosts(userId),
    queryFn: async (): Promise<PaginatedResponse<Post>> => {
      return userApi.getUserPosts(userId);
    },
    enabled: !!userId,
    ...options,
  });
};

export const useCreatePost = (options?: UseMutationOptions<Post, Error, CreatePostRequest>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (postData: CreatePostRequest) => {
      return postsApi.createPost(postData);
    },
    onSuccess: (newPost) => {
      // Invalidate posts queries to refetch
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.userPosts(newPost.userId) });
      
      // Add to cache
      queryClient.setQueryData(queryKeys.post(newPost.postId), newPost);
    },
    ...options,
  });
};

export const useUpdatePost = (options?: UseMutationOptions<Post, Error, { postId: string; updates: Partial<CreatePostRequest> }>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ postId, updates }: { postId: string; updates: Partial<CreatePostRequest> }) => {
      return postsApi.updatePost(postId, updates);
    },
    onSuccess: (updatedPost) => {
      queryClient.setQueryData(queryKeys.post(updatedPost.postId), updatedPost);
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.userPosts(updatedPost.userId) });
    },
    ...options,
  });
};

export const useDeletePost = (options?: UseMutationOptions<void, Error, string>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (postId: string) => {
      return postsApi.deletePost(postId);
    },
    onSuccess: (_, postId) => {
      queryClient.removeQueries({ queryKey: queryKeys.post(postId) });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['userPosts'] });
    },
    ...options,
  });
};

// Messages Hooks
export const useConversations = (options?: UseQueryOptions<PaginatedResponse<Conversation>, Error>) => {
  return useQuery({
    queryKey: queryKeys.conversations(),
    queryFn: async () => {
      return messagingApi.getConversations();
    },
    ...options,
  });
};

export const useMessages = (conversationId: string, options?: UseQueryOptions<PaginatedResponse<Message>, Error>) => {
  return useQuery({
    queryKey: queryKeys.messages(conversationId),
    queryFn: async () => {
      return messagingApi.getMessages(conversationId);
    },
    enabled: !!conversationId,
    ...options,
  });
};

export const useSendMessage = (options?: UseMutationOptions<Message, Error, {
  conversationId?: string;
  receiverId: string;
  postId?: string;
  content: string;
  messageType: Message['messageType'];
  offerAmount?: number;
}>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (messageData) => {
      return messagingApi.sendMessage(messageData);
    },
    onSuccess: (newMessage) => {
      // Update conversations list
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations() });
      
      // Update messages for this conversation
      if (newMessage.conversationId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.messages(newMessage.conversationId) });
      }
    },
    ...options,
  });
};

// Upload Hook
export const useUploadUrl = (options?: UseMutationOptions<{
  uploadUrl: string;
  fileUrl: string;
  fields?: { [key: string]: string };
}, Error, { fileName: string; fileType: string; fileSize: number }>) => {
  return useMutation({
    mutationFn: async (fileData) => {
      return uploadApi.getUploadUrl(fileData);
    },
    ...options,
  });
};

export const useFileUpload = (options?: UseMutationOptions<string, Error, {
  file: File;
}>) => {
  const uploadUrlMutation = useUploadUrl();
  
  return useMutation({
    mutationFn: async ({ file }) => {
      // Get upload URL
      const uploadData = await uploadUrlMutation.mutateAsync({
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
      });
      
      // Upload file
      await uploadApi.uploadFile(file, uploadData);
      
      // Return the file URL
      return uploadData.fileUrl;
    },
    ...options,
  });
}; 